import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { dashboardService, DashboardData } from '../services/dashboard';
import { getActiveRestaurantId } from '../utils/roleAccess';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Users,
  UtensilsCrossed,
  IndianRupee,
  Clock,
  CheckCircle,
  AlertCircle,
  Activity,
  Calendar,
  BarChart3,
  RefreshCw,
  FileText,
  CreditCard,
  Package
} from 'lucide-react';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import { SkeletonCard } from '../components/LoadingStates';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();

  const activeRestaurantId = getActiveRestaurantId();

  const { data: dashboardData, isLoading, refetch, isFetching } = useQuery<DashboardData>(
    ['dashboard', user?.organizationCode, activeRestaurantId],
    () => dashboardService.getDashboardData(user?.organizationCode!, activeRestaurantId),
    {
      enabled: !!user?.organizationCode,
      refetchInterval: 60000 // Refresh every minute
    }
  );

  const COLORS = [
    currentTheme.colors.primary[600],    // Primary theme color
    '#10b981',                            // Green
    '#3b82f6',                            // Blue
    '#f59e0b',                            // Amber/Yellow
    '#ef4444',                            // Red
    '#8b5cf6',                            // Purple
    '#ec4899'                             // Pink
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
        {/* Header Skeleton */}
        <SkeletonCard />

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
          <div className="lg:col-span-2">
            <SkeletonCard />
          </div>
          <SkeletonCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          <SkeletonCard />
          <SkeletonCard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      {/* Welcome Header */}
      <div
        className="relative overflow-hidden rounded-2xl sm:rounded-3xl p-5 sm:p-7 text-white"
        style={{
          background: `linear-gradient(135deg, ${currentTheme.colors.primary[600]}, ${currentTheme.colors.primary[800]})`,
          boxShadow: `0 24px 50px -22px ${currentTheme.colors.primary[700]}`
        }}
      >
        <div className="pointer-events-none absolute -top-20 -right-10 h-56 w-56 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)' }} />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">
              Welcome back, {user?.firstName}! 👋
            </h1>
            <p className="text-white/90 text-sm sm:text-base">
              {user?.organizationName || user?.organizationCode} • {format(new Date(), 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="p-2 sm:p-2.5 bg-white/20 hover:bg-white/30 rounded-lg transition-all disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 sm:h-5 sm:w-5 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {/* Today's Revenue */}
        <div
          className="rounded-2xl border p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
              <IndianRupee className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            {dashboardData?.salesTrend?.comparison?.revenueGrowth !== 0 && (
              <div className={`flex items-center px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${
                dashboardData?.salesTrend?.comparison?.positive
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {dashboardData?.salesTrend?.comparison?.positive ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                {Math.abs(dashboardData?.salesTrend?.comparison?.revenueGrowth || 0)}%
              </div>
            )}
          </div>
          <p className="text-xs sm:text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Today's Revenue
          </p>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
            ₹{dashboardData?.overview?.todayRevenue?.toLocaleString() || '0'}
          </p>
        </div>

        {/* Active Orders */}
        <div
          className="rounded-2xl border p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
          onClick={() => navigate('/orders')}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs sm:text-sm font-medium">
              {dashboardData?.overview?.todayOrders || 0} today
            </div>
          </div>
          <p className="text-xs sm:text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Active Orders
          </p>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
            {dashboardData?.overview?.activeOrders || 0}
          </p>
        </div>

        {/* Available Tables */}
        <div
          className="rounded-2xl border p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
          onClick={() => navigate('/tables')}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
          </div>
          <p className="text-xs sm:text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Available Tables
          </p>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
            {dashboardData?.overview?.availableTables || 0}
            <span className="text-base sm:text-lg font-normal text-gray-400">
              /{dashboardData?.overview?.totalTables || 0}
            </span>
          </p>
        </div>

        {/* Menu Items */}
        <div
          className="rounded-2xl border p-4 sm:p-5 transition-all duration-200 hover:-translate-y-1 cursor-pointer shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
          onClick={() => navigate('/items')}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="p-2.5 sm:p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl shadow-lg">
              <UtensilsCrossed className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            {(dashboardData?.overview?.outOfStockItems || 0) > 0 && (
              <div className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {dashboardData?.overview?.outOfStockItems} out
              </div>
            )}
          </div>
          <p className="text-xs sm:text-sm font-medium mb-1" style={{ color: currentTheme.colors.text.secondary }}>
            Available Items
          </p>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ color: currentTheme.colors.text.primary }}>
            {dashboardData?.overview?.availableItems || 0}
            <span className="text-base sm:text-lg font-normal text-gray-400">
              /{dashboardData?.overview?.totalItems || 0}
            </span>
          </p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
        {/* Today's Summary - 2 columns */}
        <div
          className="lg:col-span-2 rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <h3 className="text-base sm:text-lg font-semibold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
              Today's Summary
            </h3>
            <span className="text-xs sm:text-sm px-2 py-1 bg-gray-100 rounded-full" style={{ color: currentTheme.colors.text.secondary }}>
              {format(new Date(), 'MMM dd, yyyy')}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
              <p className="text-xs sm:text-sm text-blue-600 mb-1">Total Orders</p>
              <p className="text-xl sm:text-2xl font-bold text-blue-900">
                {dashboardData?.todaySummary?.totalOrders || 0}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <p className="text-xs sm:text-sm text-green-600 mb-1">Gross Amount</p>
              <p className="text-xl sm:text-2xl font-bold text-green-900">
                ₹{dashboardData?.todaySummary?.grossAmount?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg">
              <p className="text-xs sm:text-sm text-red-600 mb-1">Discount</p>
              <p className="text-xl sm:text-2xl font-bold text-red-900">
                ₹{dashboardData?.todaySummary?.discountGiven?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
              <p className="text-xs sm:text-sm text-purple-600 mb-1">Tax Collected</p>
              <p className="text-xl sm:text-2xl font-bold text-purple-900">
                ₹{dashboardData?.todaySummary?.taxAmount?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
              <p className="text-xs sm:text-sm text-green-600 mb-1">Net Revenue</p>
              <p className="text-xl sm:text-2xl font-bold text-green-900">
                ₹{dashboardData?.todaySummary?.netRevenue?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="p-3 sm:p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg">
              <p className="text-xs sm:text-sm text-yellow-600 mb-1">Avg Order</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-900">
                ₹{dashboardData?.todaySummary?.avgOrderValue?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        {/* Table Status - 1 column */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-5 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <Users className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Table Status
          </h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-sm sm:text-base font-medium text-green-900">Available</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-green-700">
                {dashboardData?.tableStatus?.available || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full mr-3"></div>
                <span className="text-sm sm:text-base font-medium text-blue-900">Occupied</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-blue-700">
                {dashboardData?.tableStatus?.occupied || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-yellow-500 rounded-full mr-3"></div>
                <span className="text-sm sm:text-base font-medium text-yellow-900">Reserved</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-yellow-700">
                {dashboardData?.tableStatus?.reserved || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full mr-3"></div>
                <span className="text-sm sm:text-base font-medium text-red-900">Out of Service</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-red-700">
                {dashboardData?.tableStatus?.outOfService || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {/* Sales Trend Chart */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            7-Day Sales Trend
          </h3>
          {dashboardData?.salesTrend?.daily && dashboardData.salesTrend.daily.length > 0 ? (
            <ResponsiveContainer width="100%" height={250} className="sm:!h-[280px]">
              <LineChart data={dashboardData.salesTrend.daily}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                  tick={{ fontSize: 11 }}
                  className="sm:!text-xs"
                />
                <YAxis tick={{ fontSize: 11 }} className="sm:!text-xs" />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    if (name === 'Revenue') return `₹${value.toLocaleString()}`;
                    return `#${value.toLocaleString()}`;
                  }}
                  labelFormatter={(label) => format(new Date(label), 'MMM dd, yyyy')}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue" />
                <Line type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} name="Orders" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] sm:h-[280px] flex items-center justify-center text-gray-400">
              <p className="text-sm">No data available</p>
            </div>
          )}
        </div>

        {/* Top Selling Items */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <Package className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Top Selling Items
          </h3>
          {dashboardData?.topItems && dashboardData.topItems.length > 0 ? (
            <ResponsiveContainer width="100%" height={250} className="sm:!h-[280px]">
              <BarChart data={dashboardData.topItems.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="itemName"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 10 }}
                  className="sm:!text-xs"
                />
                <YAxis tick={{ fontSize: 11 }} className="sm:!text-xs" />
                <Tooltip contentStyle={{ fontSize: '12px' }} />
                <Bar dataKey="quantitySold" fill={currentTheme.colors.primary[600]} name="Quantity Sold" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] sm:h-[280px] flex items-center justify-center text-gray-400">
              <p className="text-sm">No data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Methods & Category Sales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {/* Payment Methods */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Payment Methods
          </h3>
          {dashboardData?.paymentMethods && dashboardData.paymentMethods.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={220} className="sm:!h-[240px]">
                <RechartsPieChart>
                  <Pie
                    data={dashboardData.paymentMethods}
                    cx="50%"
                    cy="50%"
                    outerRadius={60}
                    className="sm:!outerRadius-20"
                    dataKey="amount"
                    label={({ method, percentage }) => `${method+" :"} ${percentage}%`}
                    labelLine={false}
                  >
                    {dashboardData.paymentMethods.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: any, name: string, props: any) => [`₹${value.toLocaleString()}`, props.payload.method]}
                    contentStyle={{ fontSize: '12px' }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 sm:gap-3 mt-3">
                {dashboardData.paymentMethods.map((method, index) => (
                  <div key={method.method} className="flex items-center">
                    <div
                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full mr-1.5 sm:mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <span className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                      {method.method} ({method.count})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-[220px] sm:h-[240px] flex items-center justify-center text-gray-400">
              <p className="text-sm">No data available</p>
            </div>
          )}
        </div>

        {/* Category-wise Sales */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            Category Performance
          </h3>
          {dashboardData?.categoryWiseSales && dashboardData.categoryWiseSales.length > 0 ? (
            <div className="space-y-3">
              {dashboardData.categoryWiseSales.map((category, index) => {
                // Determine color based on category name
                let barColor = COLORS[index % COLORS.length];
                if (category.category.toLowerCase().includes('veg')) {
                  barColor = category.category.toLowerCase().includes('non') ? '#ef4444' : '#10b981';
                }

                return (
                  <div key={category.category} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm sm:text-base font-medium" style={{ color: currentTheme.colors.text.primary }}>
                          {category.category}
                        </span>
                        {category.category.toLowerCase().includes('veg') && !category.category.toLowerCase().includes('non') && (
                          <span className="text-xs">🟢</span>
                        )}
                        {category.category.toLowerCase().includes('non') && (
                          <span className="text-xs">🔴</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-sm sm:text-base font-bold" style={{ color: currentTheme.colors.text.primary }}>
                          ₹{category.revenue?.toLocaleString()}
                        </span>
                        <span className="text-xs sm:text-sm text-gray-500 ml-2">
                          ({category.itemsSold} items)
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${category.percentage || 0}%`,
                          backgroundColor: barColor
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-[220px] sm:h-[240px] flex items-center justify-center text-gray-400">
              <p className="text-sm">No data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders & Outstanding Dues */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
        {/* Recent Orders */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <div className="p-4 sm:p-5 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
            <h3 className="text-base sm:text-lg font-semibold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
              Recent Orders
            </h3>
          </div>
          <div className="p-4 sm:p-5 max-h-[400px] overflow-y-auto">
            {dashboardData?.recentOrders && dashboardData.recentOrders.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.recentOrders.slice(0, 5).map((order) => (
                  <div
                    key={order.orderId}
                    className="flex items-center justify-between p-3 rounded-lg border hover:shadow-sm transition-all cursor-pointer"
                    style={{ borderColor: currentTheme.colors.border.primary }}
                    onClick={() => navigate('/orders')}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                          {order.orderNo || order.orderNumber}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {order.status}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500">
                        {order.tableNumber} • {order.customerName} • {order.items} items • {order.timeElapsed}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm sm:text-base font-bold" style={{ color: currentTheme.colors.text.primary }}>
                        ₹{order.amount?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <ShoppingCart className="h-12 w-12 mb-2 opacity-50" />
                <p className="text-sm">No recent orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Outstanding Dues */}
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)]"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <div className="p-4 sm:p-5 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                Outstanding Dues
              </h3>
              <div className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                {dashboardData?.outstandingDues?.count || 0} pending
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 max-h-[400px] overflow-y-auto">
            {dashboardData?.outstandingDues?.dues && dashboardData.outstandingDues.dues.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.outstandingDues.dues.slice(0, 5).map((due) => (
                  <div
                    key={due.orderId}
                    className="flex items-center justify-between p-3 rounded-lg border hover:shadow-sm transition-all"
                    style={{ borderColor: currentTheme.colors.border.primary }}
                  >
                    <div className="flex-1">
                      <p className="text-sm font-semibold mb-1" style={{ color: currentTheme.colors.text.primary }}>
                        {due.customerName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {due.customerMobile} • {format(new Date(due.orderDate), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        {due.daysOverdue} days overdue
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm sm:text-base font-bold text-red-600">
                        ₹{due.amount?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <CheckCircle className="h-12 w-12 mb-2 opacity-50 text-green-500" />
                <p className="text-sm">No outstanding dues</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Alerts */}
      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <div
          className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5"
          style={{
            backgroundColor: currentTheme.colors.background.card,
            borderColor: currentTheme.colors.border.primary
          }}
        >
          <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-red-500" />
            Alerts & Notifications
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {dashboardData.alerts.map((alert, index) => (
              <div
                key={index}
                className={`p-3 sm:p-4 rounded-lg border-l-4 ${
                  alert.priority === 'high' ? 'bg-red-50 border-red-500' :
                  alert.priority === 'medium' ? 'bg-yellow-50 border-yellow-500' :
                  'bg-blue-50 border-blue-500'
                }`}
              >
                <p className="text-xs text-gray-500 mb-1">
                  {format(new Date(alert.timestamp), 'MMM dd, HH:mm')}
                </p>
                <p className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                  {alert.message}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div
        className="rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_30px_-16px_rgba(16,24,40,0.20)] p-4 sm:p-5 md:p-6"
        style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}
      >
        <h3 className="text-base sm:text-lg font-semibold mb-4" style={{ color: currentTheme.colors.text.primary }}>
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/place-order')}
            className="flex flex-col items-center p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md hover:scale-105"
            style={{
              borderColor: currentTheme.colors.border.primary,
              color: currentTheme.colors.text.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="p-2 sm:p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl mb-2">
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center">New Order</span>
          </button>
          <button
            onClick={() => navigate('/items')}
            className="flex flex-col items-center p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md hover:scale-105"
            style={{
              borderColor: currentTheme.colors.border.primary,
              color: currentTheme.colors.text.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="p-2 sm:p-3 bg-gradient-to-br from-green-500 to-green-600 rounded-xl mb-2">
              <UtensilsCrossed className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center">Manage Items</span>
          </button>
          <button
            onClick={() => navigate('/tables')}
            className="flex flex-col items-center p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md hover:scale-105"
            style={{
              borderColor: currentTheme.colors.border.primary,
              color: currentTheme.colors.text.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mb-2">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center">Manage Tables</span>
          </button>
          <button
            onClick={() => navigate('/orders')}
            className="flex flex-col items-center p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md hover:scale-105"
            style={{
              borderColor: currentTheme.colors.border.primary,
              color: currentTheme.colors.text.primary
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mb-2">
              <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <span className="text-xs sm:text-sm font-medium text-center">View Orders</span>
          </button>
        </div>
      </div>
    </div>
  );
};
